'use client';

import classNames from 'classnames';
import { cloneElement, type PropsWithChildren, type ReactElement, useRef } from 'react';
import {
  type AriaButtonProps,
  type AriaModalOverlayProps,
  Overlay,
  useModalOverlay,
  useOverlayTrigger,
} from 'react-aria';
import {
  type OverlayTriggerProps,
  type OverlayTriggerState,
  useOverlayTriggerState,
} from 'react-stately';

import styles from './modal.module.scss';

const Modal = ({
  children,
  containerClassName,
  overlayClassName,
  state,
  ...props
}: PropsWithChildren<
  AriaModalOverlayProps & {
    containerClassName?: string;
    overlayClassName?: string;
    state: OverlayTriggerState;
  }
>) => {
  const ref = useRef(null);
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);

  return (
    <Overlay>
      <div {...underlayProps} className={classNames(styles['overlay'], overlayClassName)}>
        <div
          {...modalProps}
          className={classNames(styles['container'], containerClassName)}
          ref={ref}
        >
          {children}
        </div>
      </div>
    </Overlay>
  );
};

export const ModalTrigger = ({
  children,
  containerClassName,
  overlayClassName,
  renderTrigger,
  ...props
}: OverlayTriggerProps & {
  children: (handleClose: () => void) => ReactElement;
  containerClassName?: string;
  overlayClassName?: string;
  renderTrigger?: (
    props: Omit<
      AriaButtonProps<'button'>,
      'onPress' | 'onPressChange' | 'onPressEnd' | 'onPressStart' | 'onPressUp'
    > & { handleModalOpen: () => void }
  ) => ReactElement;
}) => {
  const state = useOverlayTriggerState(props);
  const {
    overlayProps,
    triggerProps: {
      onPress,
      onPressChange,
      onPressEnd,
      onPressStart,
      onPressUp,
      ...restTriggerProps
    },
  } = useOverlayTrigger({ type: 'dialog' }, state);

  return (
    <>
      {renderTrigger &&
        renderTrigger({ ...restTriggerProps, handleModalOpen: () => state.setOpen(true) })}
      {state.isOpen && (
        <Modal
          containerClassName={containerClassName}
          overlayClassName={overlayClassName}
          state={state}
        >
          {cloneElement(children(state.close), overlayProps)}
        </Modal>
      )}
    </>
  );
};
